/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable jsx-a11y/label-has-associated-control */
import * as React from 'react';
import { Country } from 'react-phone-number-input';
import moment from 'moment';
import {
  Input,
  PhoneNumberInput,
  Select,
  TextBox,
  ToggleInput,
} from '../../index';
import useValidator from '../../../hooks/useValidator';
import { MiscFormActions } from '../../../pages/Misc/Misc';
import countries from '../../../utils/countries';
import { User } from '../../../context/authContext';

export type UserFormDataType = {
  userId?: string;
  surname: string;
  othernames: string;
  email: string;
  phoneNumber: string;
  address: string;
  otherAddress: string;
  city: string;
  state: string;
  postalCode: string;
  countryCode: string;
  notes: string;
  role: string;
  password: string;
  confirmPassword?: string;
  isActive: boolean;
};

type UserProps = {
  store: string;
  user: User | undefined;
};

const UserForm = React.forwardRef<MiscFormActions, UserProps>(
  ({ user, store }, ref) => {
    const [account, setAccount] = React.useState({
      surname: user?.surname || '',
      othernames: user?.othernames || '',
      email: user?.email || '',
      phoneNumber: user?.phoneNumber || '',
      address: user?.address || '',
      otherAddress: user?.otherAddress || '',
      city: user?.city || '',
      state: user?.state || '',
      postalCode: user?.postalCode || '',
      countryCode: user?.countryCode || 'GH',
      notes: user?.notes || '',
      role: user?.role || 'Employee',
      password: '',
      confirmPassword: '',
      isActive: user ? user?.isActive : true,
    });

    const [errors, setErrors] = React.useState({
      surname: false,
      othernames: false,
      email: false,
      phoneNumber: false,
      password: false,
      confirmPassword: false,
    });

    const [validator, showValidationMessage] = useValidator({});

    React.useImperativeHandle(ref, () => {
      return {
        getFormData: userFormData,
      };
    });

    const handleAccountInputChange = (
      event: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >,
    ) => {
      const { name, value } = event.target;

      setAccount(fndAccount => ({
        ...fndAccount,
        [name]: value,
      }));
    };

    const userFormData = (): UserFormDataType | null => {
      if (!validator.allValid()) {
        setErrors(errs => ({
          ...errs,
          surname: !validator.fieldValid('surname'),
          othernames: !validator.fieldValid('othernames'),
          email: !validator.fieldValid('email'),
          password: !validator.fieldValid('password'),
          phoneNumber: !validator.fieldValid('phoneNumber'),
          confirmPassword: !validator.fieldValid('confirmPassword'),
        }));
        showValidationMessage(true);
        return null;
      }

      const fndAccount = {
        ...account,
        ...(user && user?.userId && { userId: user.userId }),
      };

      return fndAccount;
    };

    return (
      <div className="h-full bg-white">
        <form className="w-full h-full flex flex-row space-x-10 items-start">
          <div className="rounded-md w-[50%] border border-[rgba(84,174,255,0.4)]">
            <div
              className="h-fit py-2 px-5 bg-[#ddf4ff] rounded-tl-md rounded-tr-md border-b
          border-[rgba(84,174,255,0.4)]"
            >
              <h2 className="text-xbase font-semibold">Details</h2>
            </div>
            <div className="pt-3 pb-5 px-5">
              <div className="space-y-4">
                <div className="flex flex-row gap-5">
                  <div className="w-full">
                    <label
                      htmlFor="othernames"
                      className="text-xbase block mb-1"
                    >
                      First name{' '}
                      <span className="text-xs font-light text-pos-red">*</span>
                    </label>
                    <Input
                      id="othernames"
                      name="othernames"
                      value={account.othernames}
                      onChange={handleAccountInputChange}
                      onBlur={() => {
                        setErrors(errs => ({
                          ...errs,
                          othernames: !validator.check(
                            account.othernames,
                            'required|min:2',
                          ),
                        }));
                      }}
                      error={errors.othernames}
                    />
                    {validator.message(
                      'othernames',
                      account.othernames,
                      'required|min:2',
                      {
                        messages: {
                          required: 'First name is required.',
                        },
                        className:
                          'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                      },
                    )}
                  </div>
                  <div className="w-full">
                    <label htmlFor="surname" className="text-xbase block mb-1">
                      Last name{' '}
                      <span className="text-xs font-light text-pos-red">*</span>
                    </label>
                    <Input
                      id="surname"
                      name="surname"
                      value={account.surname}
                      onChange={handleAccountInputChange}
                      onBlur={() => {
                        setErrors(errs => ({
                          ...errs,
                          surname: !validator.check(
                            account.surname,
                            'required|min:2',
                          ),
                        }));
                      }}
                      error={errors.surname}
                    />
                    {validator.message(
                      'surname',
                      account.surname,
                      'required|min:2',
                      {
                        messages: {
                          required: 'Last name is required.',
                        },
                        className:
                          'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                      },
                    )}
                  </div>
                </div>
                <div className="flex flex-row gap-5">
                  <div className="flex-1 w-full">
                    <label htmlFor="email" className="text-xbase block mb-1">
                      Email{' '}
                      <span className="text-xs font-light text-pos-red">*</span>
                    </label>
                    <Input
                      id="email"
                      name="email"
                      autoComplete="username"
                      value={account.email}
                      type="email"
                      onChange={handleAccountInputChange}
                      onBlur={() => {
                        setErrors(errs => ({
                          ...errs,
                          email: !validator.check(
                            account.email,
                            'required|email',
                          ),
                        }));
                      }}
                      error={errors.email}
                    />
                    {validator.message(
                      'email',
                      account.email,
                      'required|email',
                      {
                        messages: {
                          required: 'Email address is required.',
                        },
                        className:
                          'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                      },
                    )}
                  </div>
                  <div className="w-[40%]">
                    <label
                      htmlFor="phoneNumber"
                      className="text-xbase block mb-1"
                    >
                      Phone
                    </label>
                    <PhoneNumberInput
                      id="phoneNumber"
                      name="phoneNumber"
                      defaultCountry={
                        account.countryCode
                          ? (account.countryCode as Country)
                          : 'GH'
                      }
                      value={account.phoneNumber}
                      onChange={value =>
                        setAccount(fndAccount => ({
                          ...fndAccount,
                          phoneNumber: value,
                        }))
                      }
                      onBlur={() => {
                        setErrors(errs => ({
                          ...errs,
                          phoneNumber: !validator.check(
                            account.phoneNumber,
                            'phone',
                          ),
                        }));
                      }}
                      error={errors.phoneNumber}
                    />
                    {validator.message(
                      'phoneNumber',
                      account.phoneNumber,
                      'phone',
                      {
                        className:
                          'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                      },
                    )}
                  </div>
                </div>
                <div className="flex flex-row gap-5">
                  <div className="w-full">
                    <label htmlFor="password" className="text-xbase block mb-1">
                      Password{' '}
                      <span className="text-xs font-light text-pos-red">*</span>
                    </label>
                    <Input
                      id="password"
                      name="password"
                      value={account.password}
                      type="password"
                      onChange={handleAccountInputChange}
                      autoComplete="new-password"
                      onBlur={() => {
                        setErrors(errs => ({
                          ...errs,
                          password: !validator.check(
                            account.password,
                            `${!user ? 'required|min:8' : 'min:8'}`,
                          ),
                        }));
                      }}
                      error={errors.password}
                    />
                    {validator.message(
                      'password',
                      account.password,
                      `${!user ? 'required|min:8' : 'min:8'}`,
                      {
                        messages: {
                          required: 'Password is required for login.',
                        },
                        className:
                          'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                      },
                    )}
                  </div>
                  <div className="w-full">
                    <label
                      htmlFor="confirmPassword"
                      className="text-xbase block mb-1"
                    >
                      Confirm Password{' '}
                      <span className="text-xs font-light text-pos-red">*</span>
                    </label>
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      value={account.confirmPassword}
                      type="password"
                      onChange={handleAccountInputChange}
                      autoComplete="new-password"
                      onBlur={() => {
                        setErrors(errs => ({
                          ...errs,
                          confirmPassword: !validator.check(
                            account.confirmPassword,
                            `${!user ? 'required|min:8' : 'min:8'}`,
                          ),
                        }));
                      }}
                      error={errors.confirmPassword}
                    />
                    {validator.message(
                      'confirmPassword',
                      account.confirmPassword,
                      `${!user ? 'required|min:8' : 'min:8'}`,
                      {
                        messages: {
                          required: 'Confirm password must match password.',
                        },
                        className:
                          'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                      },
                    )}
                  </div>
                </div>
              </div>
              <div className="space-y-4 mt-6">
                <div className="border-b border-[#d0d7de]">
                  <h2 className="text-xbase font-semibold">User Status</h2>
                </div>
                <div className="flex flex-row space-x-6">
                  <div>
                    <label htmlFor="role" className="text-xbase block mb-1">
                      Role
                    </label>
                    <Select
                      placeholder={{
                        value: '',
                        label: 'Role',
                      }}
                      value={account.role}
                      options={[
                        {
                          value: 'Admin',
                          label: 'Admin',
                        },
                        {
                          value: 'Manager',
                          label: 'Manager',
                        },
                        {
                          value: 'Employee',
                          label: 'Employee',
                        },
                      ]}
                      name="role"
                      id="role"
                      onChange={handleAccountInputChange}
                      className="w-[200px]"
                    />
                  </div>
                  <div className="">
                    <label htmlFor="isActive" className="text-xbase block mb-1">
                      Is Active
                    </label>
                    <ToggleInput
                      id="isActive"
                      name="isActive"
                      value={account.isActive.toString()}
                      checked={account.isActive}
                      onChange={event =>
                        setAccount(fndAccount => ({
                          ...fndAccount,
                          isActive: event.target.checked,
                        }))
                      }
                      className="align-middle cursor-pointer"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="taxes" className="text-xbase block mb-1">
                    Default Store
                  </label>
                  <Input
                    id="store"
                    name="store"
                    className="w-[200px]"
                    disabled
                    value={store}
                    onChange={() => false}
                  />
                </div>
              </div>
              <div className="mt-6">
                <div className="border-b border-[#d0d7de]">
                  <h2 className="text-xbase font-semibold">
                    Notes & Preferences
                  </h2>
                </div>
                <div className="flex flex-row mt-6">
                  <TextBox
                    id="notes"
                    name="notes"
                    value={account.notes}
                    onChange={handleAccountInputChange}
                  />
                </div>
                <div className="flex flex-row  mt-4">
                  <h2 className="text-xbase font-semibold">
                    Last Login:{' '}
                    <span className="font-light text-pos-gray">
                      {user?.lastLogin
                        ? moment(user.lastLogin).format(
                            'ddd, MMM DD, YYYY h:mm A',
                          )
                        : ' No Activity Yet'}
                    </span>
                  </h2>
                </div>
              </div>
            </div>
          </div>
          <div className="rounded-md w-[50%] border border-[rgba(84,174,255,0.4)]">
            <div
              className="h-fit py-2 px-5 bg-[#ddf4ff] rounded-tl-md rounded-tr-md border-b
          border-[rgba(84,174,255,0.4)]"
            >
              <h2 className="text-xbase font-semibold">Address</h2>
            </div>
            <div className="pt-3 pb-5 px-5">
              <div className="space-y-4">
                <div>
                  <label htmlFor="address" className="text-xbase block mb-1">
                    Address
                  </label>
                  <Input
                    name="address"
                    id="address"
                    value={account.address}
                    onChange={handleAccountInputChange}
                  />
                </div>
                <div>
                  <label
                    htmlFor="otherAddress"
                    className="text-xbase block mb-1"
                  >
                    Other Address
                  </label>
                  <Input
                    id="otherAddress"
                    name="otherAddress"
                    value={account.otherAddress}
                    onChange={handleAccountInputChange}
                  />
                </div>
                <div className="flex flex-row gap-5">
                  <div className="flex-1">
                    <label
                      htmlFor="countryCode"
                      className="text-xbase block mb-1"
                    >
                      Country
                    </label>
                    <Select
                      placeholder={{
                        value: '',
                        label: '',
                      }}
                      value={account.countryCode}
                      options={countries.map(fndCountry => ({
                        value: fndCountry.iso,
                        label: fndCountry.name,
                      }))}
                      name="countryCode"
                      id="countryCode"
                      onChange={handleAccountInputChange}
                    />
                  </div>
                  <div>
                    <label htmlFor="city" className="text-xbase block mb-1">
                      City
                    </label>
                    <Input
                      name="city"
                      value={account.city}
                      onChange={handleAccountInputChange}
                    />
                  </div>
                  <div>
                    <label htmlFor="state" className="text-xbase block mb-1">
                      State / Region
                    </label>
                    <Input
                      name="state"
                      id="state"
                      value={account.state}
                      onChange={handleAccountInputChange}
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="postalCode" className="text-xbase block mb-1">
                    Postal code
                  </label>
                  <Input
                    name="postalCode"
                    id="postalCode"
                    value={account.postalCode}
                    onChange={handleAccountInputChange}
                    className="w-[250px]"
                  />
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    );
  },
);

export default UserForm;
